/**
 * 投稿用QRコードの配布物を作る処理。
 *
 * 会社管理の「投稿用のQRコード・URL」と、会社登録が完了したときの案内の
 * 両方から使う。文面やレイアウトを直すときに1か所で済むよう、画面から切り出した。
 */

type PosterParams = {
  /** 画面に描画済みの QRCodeSVG の id */
  elementId: string;
  /** 保存するファイル名 */
  fileName: string;
  companyName: string;
  url: string;
};

/**
 * 配布用の案内文を組み立てる。
 *
 * 配る側が毎回文面を考えなくて済むようにする。
 * ブックマークを勧めているのは、/home を保存すると期限切れのたびに
 * 案内画面へ飛んでしまい、QRのURL（/q/...）を保存する必要があるため。
 */
export function buildAccessLinkMessage(companyName: string, url: string): string {
  return [
    `${companyName}のみなさまへ`,
    '',
    'LiaVoiceへの投稿はこちらからお願いします。',
    url,
    '',
    '※会社IDやパスワードの入力は必要ありません',
    '※このページをブックマークしておくと、次回から一度で開けます',
    '※このURLは他の方へ転送しないでください',
  ].join('\n');
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = src;
  });
}

/**
 * QRコードだけをPNGで保存する。
 * 既に配布物の型が決まっていて、QRコードだけ差し替えたい場合に使う。
 */
export function saveQrCodeImage(elementId: string, fileName: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const svgElement = document.getElementById(elementId) as unknown as SVGElement | null;
    if (!svgElement) {
      reject(new Error('QRコードが見つかりません'));
      return;
    }

    const svgData = new XMLSerializer().serializeToString(svgElement);
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const svgUrl = URL.createObjectURL(svgBlob);

    loadImage(svgUrl)
      .then((image) => {
        const canvas = document.createElement('canvas');
        canvas.width = image.width;
        canvas.height = image.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          throw new Error('画像を作成できません');
        }
        ctx.drawImage(image, 0, 0);
        URL.revokeObjectURL(svgUrl);

        canvas.toBlob((blob) => {
          if (!blob) {
            reject(new Error('画像を作成できません'));
            return;
          }
          downloadBlob(blob, fileName);
          resolve();
        }, 'image/png');
      })
      .catch((error) => {
        URL.revokeObjectURL(svgUrl);
        reject(error);
      });
  });
}

/**
 * 掲示・配布用の画像を保存する。
 *
 * QRコードだけを渡すと、配る側が案内文を用意する必要がある。
 * 印刷して貼る場合にも、連絡ツールで送る場合にも同じ1枚で足りるよう、
 * QRコード・URL・案内文をまとめた画像にする。
 */
export async function saveAccessLinkPoster({
  elementId,
  fileName,
  companyName,
  url,
}: PosterParams): Promise<void> {
  const svgElement = document.getElementById(elementId) as unknown as SVGElement | null;
  if (!svgElement) {
    throw new Error('QRコードが見つかりません');
  }

  const svgData = new XMLSerializer().serializeToString(svgElement);
  const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
  const svgUrl = URL.createObjectURL(svgBlob);

  try {
    const qrImage = await loadImage(svgUrl);
    // ロゴの読み込みに失敗しても、QRコードは配れるようにする
    const logoImage = await loadImage('/icon.svg').catch(() => null);

    // A4（縦）を 150dpi 相当で描く
    const WIDTH = 1240;
    const HEIGHT = 1754;
    const canvas = document.createElement('canvas');
    canvas.width = WIDTH;
    canvas.height = HEIGHT;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('画像を作成できません');
    }

    const center = WIDTH / 2;
    const font = '"Noto Sans JP", "Hiragino Sans", sans-serif';

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, WIDTH, HEIGHT);
    ctx.textAlign = 'center';

    // ロゴ
    if (logoImage) {
      const logoSize = 150;
      ctx.drawImage(logoImage, center - logoSize / 2, 150, logoSize, logoSize);
    }

    // 会社名
    ctx.fillStyle = '#666666';
    ctx.font = `34px ${font}`;
    ctx.fillText(companyName, center, 360);

    // 見出し
    ctx.fillStyle = '#000000';
    ctx.font = `bold 60px ${font}`;
    ctx.fillText('LiaVoice 投稿用QRコード', center, 440);

    // 何のアプリかが分からないまま投稿画面に来ることがないよう、
    // アプリ内の案内と同じ言い方で簡単に説明する
    ctx.fillStyle = '#444444';
    ctx.font = `34px ${font}`;
    ctx.fillText('気づいたことや感謝の気持ちを、匿名で届けられます', center, 510);
    ctx.fillStyle = '#666666';
    ctx.font = `30px ${font}`;
    ctx.fillText('みなさんの声を、より良い職場づくりに活かしていきます', center, 560);

    ctx.fillStyle = '#000000';
    ctx.font = `38px ${font}`;
    ctx.fillText('カメラで読み取ると、そのまま投稿できます', center, 650);

    // QRコード
    const qrSize = 560;
    ctx.drawImage(qrImage, center - qrSize / 2, 700, qrSize, qrSize);

    // URL（読み取れない場合の入力用）
    ctx.fillStyle = '#666666';
    ctx.font = `28px ${font}`;
    ctx.fillText('QRコードが読み取れない場合はこちら', center, 1350);
    ctx.fillStyle = '#000000';
    ctx.font = `30px ${font}`;
    ctx.fillText(url, center, 1400);

    // 注意書き
    ctx.fillStyle = '#666666';
    ctx.font = `30px ${font}`;
    const notes = [
      '会社IDやパスワードの入力は必要ありません',
      'このURLは他の方へ転送しないでください',
    ];
    notes.forEach((note, index) => {
      ctx.fillText(note, center, 1510 + index * 54);
    });

    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
    if (!blob) {
      throw new Error('画像を作成できません');
    }
    downloadBlob(blob, fileName);
  } finally {
    URL.revokeObjectURL(svgUrl);
  }
}

function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
